import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast } from './listPageStyles';
import { ListPageShell } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import './securityScreens.css';

// G-Y-02 — Kolon (alan) yetkileri. Ekran seç → alan listesi → hedef bazında görünürlük matrisi.
//
// ⚠ KK-01 union kuralının buradaki bedeli: birden fazla kaynaktan gelen görünürlükte EN GENİŞ
// olan kazanır. Yani tek bir "Gizli" satırı alanı gizlemez — kullanıcının o alana daha geniş
// görünürlük veren TÜM rollerinden çıkarılması gerekir. Ekran bu uyarıyı açıkça gösterir,
// yoksa yönetici "gizli yaptım ama görünüyor" tuzağına düşer.

const VISIBILITY = [
  { value: 0, label: 'Gizli', tone: 'danger', help: 'Alan ekranda hiç render edilmez.' },
  { value: 1, label: 'Salt-okunur', tone: 'warn', help: 'Alan görünür ama değiştirilemez.' },
  { value: 2, label: 'Düzenlenebilir', tone: 'ok', help: 'Varsayılan davranış.' },
];

const PLATFORMS = [
  { value: 0, label: 'Tümü' },
  { value: 1, label: 'Web' },
  { value: 2, label: 'Android' },
  { value: 3, label: 'iOS' },
];

const TARGET_TYPES = [
  { value: 1, label: 'Rol' },
  { value: 3, label: 'Kullanıcı' },
];

export function FieldRightsPage() {
  usePageMeta('Kolon Yetkileri', 'Kullanıcı & Yetki');
  const { canEdit } = usePageActions();

  const [menus, setMenus] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [menuId, setMenuId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState(null);

  // Yeni satır formu
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newTargetType, setNewTargetType] = useState(1);
  const [newTargetId, setNewTargetId] = useState('');
  const [newVisibility, setNewVisibility] = useState(0);
  const [newPlatform, setNewPlatform] = useState(0);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  const pull = async (url) => {
    try {
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) return [];
      const j = await res.json();
      return j?.value || j?.data || j?.Data || (Array.isArray(j) ? j : []);
    } catch { return []; }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [m, r, u] = await Promise.all([
        pull('/api/Menu/MenuTree'),
        pull('/api/Roles/Select'),
        pull('/api/Users?$select=Id,FirstName,LastName,Email&$orderby=FirstName&$top=1000'),
      ]);
      if (cancelled) return;

      const flat = [];
      const walk = (nodes) => (nodes || []).forEach((n) => {
        const id = n.id ?? n.Id;
        const path = n.path ?? n.Path;
        const title = n.title ?? n.Title ?? n.name ?? n.Name;
        if (id && path && path !== '#') flat.push({ id, label: `${title} (${id})` });
        walk(n.children ?? n.Children);
      });
      walk(m);
      setMenus(flat.sort((a, b) => a.label.localeCompare(b.label, 'tr')));

      setRoles((r || []).map((x) => ({ id: x.id ?? x.Id, label: x.name ?? x.Name ?? x.code ?? x.Code })).filter((x) => x.id));
      setUsers((u || []).map((x) => ({
        id: x.id ?? x.Id,
        label: `${x.firstName ?? x.FirstName ?? ''} ${x.lastName ?? x.LastName ?? ''}`.trim() || (x.email ?? x.Email ?? ''),
      })).filter((x) => x.id));
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    if (!menuId) { setRows([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`/api/field-rights?menuId=${menuId}`, { headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kolon yetkileri okunamadı');
      setRows(body?.data || body?.Data || []);
      setDirty(false);
    } catch (e) {
      showToast('error', e.message || 'Kolon yetkileri okunamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [menuId]);

  useEffect(() => { load(); }, [load]);

  const targetOptions = newTargetType === 1 ? roles : users;
  const labelOf = (targetType, targetId) => {
    const src = targetType === 1 ? roles : users;
    return src.find((x) => String(x.id) === String(targetId))?.label || `#${targetId}`;
  };

  const addRow = () => {
    if (!newFieldKey.trim()) { showToast('error', 'Alan anahtarı giriniz'); return; }
    if (!newTargetId) { showToast('error', 'Hedef seçiniz'); return; }

    const key = newFieldKey.trim();
    const duplicate = rows.some((r) => r.fieldKey === key
      && r.targetType === newTargetType && String(r.targetId) === String(newTargetId)
      && r.platform === newPlatform);
    if (duplicate) { showToast('error', 'Bu alan/hedef/platform için satır zaten var'); return; }

    setRows((prev) => [...prev, {
      id: 0, menuId: Number(menuId), fieldKey: key,
      targetType: newTargetType, targetId: Number(newTargetId),
      visibility: newVisibility, platform: newPlatform,
    }]);
    setNewFieldKey('');
    setDirty(true);
  };

  const removeRow = (index) => { setRows((prev) => prev.filter((_, i) => i !== index)); setDirty(true); };
  const changeVisibility = (index, value) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, visibility: Number(value) } : r)));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/field-rights?menuId=${menuId}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(rows),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kaydedilemedi');
      showToast('success', 'Kolon yetkileri kaydedildi');
      setDirty(false);
      load();
    } catch (e) {
      showToast('error', e.message || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  // Aynı alana birden fazla hedeften yetki verilmişse union sonucu ne olur? Yöneticiye ÖNCEDEN
  // göster — "gizli yaptım ama görünüyor" şikâyetinin kaynağı tam olarak budur.
  const conflicts = useMemo(() => {
    const byField = {};
    rows.forEach((r) => {
      (byField[r.fieldKey] ||= []).push(r);
    });
    return Object.entries(byField)
      .filter(([, list]) => list.length > 1 && new Set(list.map((x) => x.visibility)).size > 1)
      .map(([field, list]) => ({ field, effective: Math.max(...list.map((x) => x.visibility)), count: list.length }));
  }, [rows]);

  return (
    <div style={s.container}>
      <ListPageShell
        title="Kolon Yetkileri"
        subtitle="Bir ekrandaki alanların rol veya kullanıcı bazında görünürlüğünü belirler."
        actions={canEdit && menuId && (
          <button type="button" style={s.btnPrimary} disabled={!dirty || saving} onClick={save}>
            {saving ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        )}
        toolbar={(
          <>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span className="sec-card-label">Ekran</span>
              <select className="gp-select" value={menuId} onChange={(e) => setMenuId(e.target.value)}>
                <option value="">Seçiniz…</option>
                {menus.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </label>
            {menuId && <button style={s.btnSecondary} onClick={load}>Yenile</button>}
          </>
        )}
      >
        {!menuId ? (
          <div className="sec-card">
            <div className="sec-card-hint">
              Kolon yetkisi tanımlamak için önce bir ekran seçin. Satırı olmayan alanlar
              <strong> düzenlenebilir</strong> kabul edilir — yani kısıt koymadığınız hiçbir alan gizlenmez.
            </div>
          </div>
        ) : (
          <>
            <div className="sec-card" style={{ marginBottom: 13 }}>
              <div className="sec-card-label">Birleşim kuralı</div>
              <div className="sec-card-hint" style={{ marginTop: 6, lineHeight: 1.55 }}>
                Bir alana birden fazla kaynaktan yetki gelirse <strong>en geniş görünürlük kazanır</strong>
                {' '}(Düzenlenebilir &gt; Salt-okunur &gt; Gizli). Bir alanı gerçekten gizlemek için,
                kullanıcının o alana daha geniş görünürlük veren <strong>tüm</strong> rollerinden
                çıkarılması gerekir — tek bir "Gizli" satırı yeterli değildir.
              </div>
            </div>

            {conflicts.length > 0 && (
              <div className="sec-card sec-card--alert" style={{ marginBottom: 13 }}>
                <div className="sec-card-label">Çakışan alanlar</div>
                <div className="sec-dist" style={{ marginTop: 7 }}>
                  {conflicts.map((c) => (
                    <div className="sec-dist-row" key={c.field}>
                      <span className="sec-dist-name"><span className="c360-erp-chip">{c.field}</span></span>
                      <span className="sec-card-hint">{c.count} satır → etkin sonuç:</span>
                      <span className={`sec-badge sec-badge--${VISIBILITY[c.effective].tone}`}>
                        {VISIBILITY[c.effective].label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {canEdit && (
              <div className="sec-card" style={{ marginBottom: 13 }}>
                <div className="sec-card-label">Yeni satır</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, alignItems: 'flex-end', marginTop: 9 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span className="sec-card-label">Alan anahtarı</span>
                    <span className="gp-num">
                      <input type="text" value={newFieldKey} placeholder="örn. customer.taxNumber"
                        onChange={(e) => setNewFieldKey(e.target.value)} />
                    </span>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span className="sec-card-label">Hedef tipi</span>
                    <select className="gp-select" value={newTargetType}
                      onChange={(e) => { setNewTargetType(Number(e.target.value)); setNewTargetId(''); }}>
                      {TARGET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span className="sec-card-label">Hedef</span>
                    <select className="gp-select" value={newTargetId} onChange={(e) => setNewTargetId(e.target.value)}>
                      <option value="">Seçiniz…</option>
                      {targetOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span className="sec-card-label">Görünürlük</span>
                    <select className="gp-select" value={newVisibility} onChange={(e) => setNewVisibility(Number(e.target.value))}>
                      {VISIBILITY.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                    </select>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span className="sec-card-label">Platform</span>
                    <select className="gp-select" value={newPlatform} onChange={(e) => setNewPlatform(Number(e.target.value))}>
                      {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </label>
                  <button type="button" style={s.btnSecondary} onClick={addRow}>+ Ekle</button>
                </div>
              </div>
            )}

            <table className="c360-table" style={{ width: '100%' }}>
              <thead>
                <tr><th>Alan</th><th>Hedef tipi</th><th>Hedef</th><th>Platform</th><th style={{ width: 190 }}>Görünürlük</th><th style={{ width: 90 }}>İşlem</th></tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24 }}>Yükleniyor…</td></tr>}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24 }}>
                    Bu ekran için kolon yetkisi tanımlı değil — tüm alanlar düzenlenebilir.
                  </td></tr>
                )}
                {!loading && rows.map((r, i) => (
                  <tr key={`${r.fieldKey}-${r.targetType}-${r.targetId}-${r.platform}-${i}`}>
                    <td><span className="c360-erp-chip">{r.fieldKey}</span></td>
                    <td>{TARGET_TYPES.find((t) => t.value === r.targetType)?.label || r.targetType}</td>
                    <td>{labelOf(r.targetType, r.targetId)}</td>
                    <td>{PLATFORMS.find((p) => p.value === r.platform)?.label || r.platform}</td>
                    <td>
                      {canEdit ? (
                        <select className="gp-select" value={r.visibility} onChange={(e) => changeVisibility(i, e.target.value)}>
                          {VISIBILITY.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                        </select>
                      ) : (
                        <span className={`sec-badge sec-badge--${VISIBILITY[r.visibility].tone}`}>
                          {VISIBILITY[r.visibility].label}
                        </span>
                      )}
                    </td>
                    <td>{canEdit && <button type="button" style={s.btnDanger} onClick={() => removeRow(i)}>Sil</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </ListPageShell>
      <Toast toast={toast} />
    </div>
  );
}

export default FieldRightsPage;
